import { create } from 'zustand';
import { api } from '../lib/api';

export interface CurrentTraderUser {
  id: string;
  email: string;
  fullName: string | null;
  marketCode: 'B3' | 'FOREX' | null;
}

interface AuthState {
  user: CurrentTraderUser | null;
  /**
   * Roles do usuário (via /auth/me). Trader comum não tem role — o array fica
   * vazio. Usado pra revelar áreas extras (ex.: 'affiliate' → menu Afiliados).
   */
  roles: string[];
  status: 'unknown' | 'loading' | 'authenticated' | 'unauthenticated';
  bootstrap: () => Promise<void>;
  setSession: (u: CurrentTraderUser, roles?: string[]) => void;
  logout: () => Promise<void>;
}

/**
 * Busca as roles em /auth/me de forma best-effort: se falhar, o app segue
 * funcionando — só não mostra os itens de menu que dependem de role (menos
 * invasivo que estender o getTraderMe, que exigiria mudança na API/shared).
 */
export async function fetchRoles(): Promise<string[]> {
  try {
    const me = await api.me();
    return me.user.roles;
  } catch {
    return [];
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  roles: [],
  status: 'unknown',

  bootstrap: async () => {
    set({ status: 'loading' });
    try {
      const [me, roles] = await Promise.all([api.getTraderMe(), fetchRoles()]);
      set({
        status: 'authenticated',
        roles,
        user: {
          id: me.user.id,
          email: me.user.email,
          fullName: me.user.fullName,
          marketCode: me.trader?.marketCode ?? null,
        },
      });
    } catch {
      set({ status: 'unauthenticated', user: null, roles: [] });
    }
  },

  setSession: (u, roles = []) => set({ status: 'authenticated', user: u, roles }),

  logout: async () => {
    await api.logout().catch(() => undefined);
    set({ status: 'unauthenticated', user: null, roles: [] });
  },
}));
